import { AssistanceEditorControlEnabledConditions, AssistanceEditorForm } from './AssistanceEditorForm'
import { EditorAssistance } from '../../../models/EditorAssistance'
import { Client } from '../../../models/client'
import { firstValueFrom } from 'rxjs'
import { FeatureFlags } from '../../../shared/featureFlags'
import { addDays, addMinutes, differenceInMinutes, isSameDay } from 'date-fns'
import { AssistanceStatus, AssistanceStatusName } from '../../../models/assistanceStatus'
import type { AssistanceEditorComponent } from './assistance-editor.component'
import { distinctUntilChanged, takeUntil } from 'rxjs/operators'
import { FormControl } from '@angular/forms'
import { DateSegment } from '../../../models/DateTime/DateSegment'
import { DateTime } from '../../../models/DateTime/DateTime'
import { TimeSegment } from '../../../models/DateTime/TimeSegment'
import equal from 'fast-deep-equal/es6'
import { SimpleStash } from '../../stash/SimpleStash'

export class EditorAssistanceFormValueChangeHandler {
  private TAG = 'EditorAssistanceFormValueChangeHandler'

  constructor(
    private ctx: AssistanceEditorComponent,
    private statuses: AssistanceStatus[],
  ) {}

  private get c() {
    return this.ctx.form.controls
  }

  setValueChangesHandlers() {
    const c = this.c

    c.plannedFrom.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.ctx.unsubscribe$))
      .subscribe((value) => this.handlePlannedFromChange(value))
    c.plannedTo.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.ctx.unsubscribe$))
      .subscribe((value) => this.handlePlannedToChange(value))
    c.clientId.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.ctx.unsubscribe$))
      .subscribe((value) => this.handleClientIdChange(value))
    c.rrule.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.ctx.unsubscribe$))
      .subscribe((value) => this.handleRruleChange(value))

    this.ctx.form.valueChanges.pipe(takeUntil(this.ctx.unsubscribe$)).subscribe((_formValue) => {
      this.handleFormControlsDisabledStates()
    })
  }

  handleInitial() {
    this.handleFormControlsDisabledStates()
  }

  private ensurePlannedToStash(): SimpleStash<DateTime> {
    const inner = this.ctx.formStashes.get(+this.ctx.assistance.id)
    let stash = inner.get('plannedTo') as SimpleStash<DateTime> | undefined
    if (!stash) {
      stash = new SimpleStash<DateTime>(DateTime.fromDate(this.ctx.assistance.plannedTo))
      inner.set('plannedTo', stash)
    }
    return stash
  }

  private ensurePlannedFromStash(): SimpleStash<DateTime> {
    const inner = this.ctx.formStashes.get(+this.ctx.assistance.id)
    let stash = inner.get('plannedFrom') as SimpleStash<DateTime> | undefined
    if (!stash) {
      stash = new SimpleStash<DateTime>(DateTime.fromDate(this.ctx.assistance.plannedFrom))
      inner.set('plannedFrom', stash)
    }
    return stash
  }

  private patchPlannedTo(value: DateTime): void {
    this.ctx.form.controls.plannedTo.markAsDirty()
    this.ctx.form.controls.plannedTo.patchValue(value, { emitEvent: false })
    if (value?.isComplete) {
      this.ensurePlannedToStash().stash(value)
    }
  }

  private patchPlannedFrom(value: DateTime): void {
    this.ctx.form.controls.plannedFrom.markAsDirty()
    this.ctx.form.controls.plannedFrom.patchValue(value, { emitEvent: false })
    if (value?.isComplete) {
      this.ensurePlannedFromStash().stash(value)
    }
  }

  private handlePlannedFromChange(plannedFromValue: DateTime) {
    if (plannedFromValue.isComplete) {
      const plannedFromStash = this.ensurePlannedFromStash()
      plannedFromStash.stash(plannedFromValue)

      if (FeatureFlags.assistanceEditor_adaptiveTimeTo) {
        const plannedToValue = this.ctx.form.controls.plannedTo.value

        if (plannedToValue.isComplete) {
          this.adaptPlannedToByDelta(plannedFromStash)
          const plannedToAfterAdapt = this.ctx.form.controls.plannedTo.value
          if (this.plannedIntervalInvalidOrZero(plannedFromValue, plannedToAfterAdapt)) {
            this.autoFillPlannedTo(plannedFromValue)
          }
        } else if (plannedToValue.isPartlyComplete) {
          if (plannedToValue.time && !plannedToValue.date) {
            this.fillDateInPlannedToFromPlannedFrom(plannedFromValue, plannedToValue)
          } else if (plannedToValue.date && !plannedToValue.time) {
            this.fillTimeInPlannedToFromPlannedFrom(plannedFromValue, plannedToValue)
          }
        } else if (plannedToValue.isEmpty) {
          this.autoFillPlannedTo(plannedFromValue)
        }
      }
    } else if (plannedFromValue.isPartlyComplete && FeatureFlags.assistanceEditor_adaptiveTimeTo) {
      const plannedToValue = this.ctx.form.controls.plannedTo.value

      if (plannedFromValue.date && !plannedFromValue.time) {
        if (!plannedToValue?.date) {
          this.ctx.form.controls.plannedTo.markAsDirty()
          this.ctx.form.controls.plannedTo.patchValue(
            DateTime.fromSegments(plannedFromValue.date, plannedToValue?.time ?? null),
            { emitEvent: false },
          )
        }
      } else if (!plannedFromValue.date && plannedFromValue.time) {
        if (!plannedToValue?.time) {
          const fromWithTimeOnly = plannedFromValue.toDate()
          const toAfter = addMinutes(fromWithTimeOnly, 60)
          const newTime = TimeSegment.fromDate(toAfter)
          this.ctx.form.controls.plannedTo.markAsDirty()
          this.ctx.form.controls.plannedTo.patchValue(DateTime.fromSegments(plannedToValue?.date ?? null, newTime), {
            emitEvent: false,
          })
        }
      }
    }
  }

  private handlePlannedToChange(plannedToValue: DateTime) {
    if (plannedToValue.isComplete) {
      if (!FeatureFlags.assistanceEditor_adaptiveTimeTo) {
        return
      }

      const plannedToStash = this.ensurePlannedToStash()
      plannedToStash.stash(plannedToValue)

      const plannedFromValue = this.ctx.form.controls.plannedFrom.value

      if (plannedFromValue.isPartlyComplete) {
        if (plannedFromValue.date && !plannedFromValue.time) {
          this.fillTimeInPlannedFromFromPlannedTo(plannedToValue, plannedFromValue)
        } else if (!plannedFromValue.date && plannedFromValue.time) {
          this.fillDateInPlannedFromFromPlannedTo(plannedToValue, plannedFromValue)
        }
      } else if (plannedFromValue.isEmpty) {
        this.autoFillPlannedFrom(plannedToValue)
      } else if (plannedFromValue.isComplete) {
        this.adaptPlannedFromByDelta(plannedToStash)
        const plannedFromAfterAdapt = this.ctx.form.controls.plannedFrom.value
        const plannedToCurrent = this.ctx.form.controls.plannedTo.value
        if (this.plannedIntervalInvalidOrZero(plannedFromAfterAdapt, plannedToCurrent)) {
          this.autoFillPlannedFrom(plannedToCurrent)
        }
      }
    } else if (plannedToValue.isPartlyComplete && FeatureFlags.assistanceEditor_adaptiveTimeTo) {
      const plannedFromValue = this.ctx.form.controls.plannedFrom.value

      if (plannedToValue.date && !plannedToValue.time) {
        if (!plannedFromValue?.date) {
          this.ctx.form.controls.plannedFrom.markAsDirty()
          this.ctx.form.controls.plannedFrom.patchValue(
            DateTime.fromSegments(plannedToValue.date, plannedFromValue?.time ?? null),
            { emitEvent: false },
          )
        }
      } else if (!plannedToValue.date && plannedToValue.time) {
        if (!plannedFromValue?.time) {
          const toWithTimeOnly = plannedToValue.toDate()
          const fromAfter = addMinutes(toWithTimeOnly, -60)
          const newTime = TimeSegment.fromDate(fromAfter)
          this.ctx.form.controls.plannedFrom.markAsDirty()
          this.ctx.form.controls.plannedFrom.patchValue(
            DateTime.fromSegments(plannedFromValue?.date ?? null, newTime),
            { emitEvent: false },
          )
        }
      }
    }
  }

  private plannedIntervalInvalidOrZero(plannedFrom: DateTime, plannedTo: DateTime): boolean {
    if (!plannedFrom.isComplete || !plannedTo.isComplete) {
      return false
    }
    return plannedTo.toDate().getTime() <= plannedFrom.toDate().getTime()
  }

  private buildPlannedToDateTimeOneHourAfterStart(
    plannedFromValue: DateTime,
    existingPlannedToDate: DateSegment,
  ): DateTime {
    const fromDate = plannedFromValue.toDate()
    const toAfterHour = addMinutes(fromDate, 60)
    let newDate: DateSegment

    if (equal(existingPlannedToDate, plannedFromValue.date)) {
      const crossesDay = !isSameDay(toAfterHour, fromDate)
      newDate = crossesDay ? DateSegment.fromDate(addDays(fromDate, 1)) : existingPlannedToDate
    } else {
      newDate = existingPlannedToDate
    }

    return DateTime.fromSegments(newDate, TimeSegment.fromDate(toAfterHour))
  }

  private buildPlannedFromDateTimeOneHourBeforeEnd(
    plannedToValue: DateTime,
    existingPlannedFromDate: DateSegment,
  ): DateTime {
    const toDate = plannedToValue.toDate()
    const fromAfterSubtract = addMinutes(toDate, -60)
    let newDate: DateSegment

    if (equal(existingPlannedFromDate, plannedToValue.date)) {
      const crossesDay = !isSameDay(fromAfterSubtract, toDate)
      newDate = crossesDay ? DateSegment.fromDate(addDays(toDate, -1)) : existingPlannedFromDate
    } else {
      newDate = existingPlannedFromDate
    }

    return DateTime.fromSegments(newDate, TimeSegment.fromDate(fromAfterSubtract))
  }

  private fillDateInPlannedToFromPlannedFrom(plannedFromValue: DateTime, plannedToValue: DateTime): void {
    if (
      !plannedFromValue.isComplete ||
      plannedFromValue.date === null ||
      plannedToValue.date !== null ||
      plannedToValue.time == null
    ) {
      return
    }

    const sameDayTo = DateTime.fromSegments(plannedFromValue.date, plannedToValue.time)
    const isOvernight = sameDayTo.toDate() < plannedFromValue.toDate()
    const targetDate = isOvernight ? DateSegment.fromDate(addDays(plannedFromValue.toDate(), 1)) : plannedFromValue.date

    this.patchPlannedTo(DateTime.fromSegments(targetDate, plannedToValue.time))
  }

  private fillTimeInPlannedToFromPlannedFrom(plannedFromValue: DateTime, plannedToValue: DateTime): void {
    if (!plannedFromValue.isComplete || !plannedToValue.date || plannedToValue.time != null) {
      return
    }

    const nextPlannedTo = this.buildPlannedToDateTimeOneHourAfterStart(plannedFromValue, plannedToValue.date)
    this.patchPlannedTo(nextPlannedTo)
  }

  private fillDateInPlannedFromFromPlannedTo(plannedToValue: DateTime, plannedFromValue: DateTime): void {
    if (
      !plannedToValue.isComplete ||
      plannedToValue.date === null ||
      plannedFromValue.date !== null ||
      plannedFromValue.time == null
    ) {
      return
    }

    const sameDayFrom = DateTime.fromSegments(plannedToValue.date, plannedFromValue.time)
    const isReverseOvernight = sameDayFrom.toDate() > plannedToValue.toDate()
    const targetDate = isReverseOvernight
      ? DateSegment.fromDate(addDays(plannedToValue.toDate(), -1))
      : plannedToValue.date

    this.patchPlannedFrom(DateTime.fromSegments(targetDate, plannedFromValue.time))
  }

  private fillTimeInPlannedFromFromPlannedTo(plannedToValue: DateTime, plannedFromValue: DateTime): void {
    if (!plannedToValue.isComplete || !plannedFromValue.date || plannedFromValue.time != null) {
      return
    }

    const nextPlannedFrom = this.buildPlannedFromDateTimeOneHourBeforeEnd(plannedToValue, plannedFromValue.date)
    this.patchPlannedFrom(nextPlannedFrom)
  }

  private adaptPlannedToByDelta(plannedFromStash: SimpleStash<DateTime>) {
    if (
      plannedFromStash.isChanged((a, b) => equal(a.time, b.time)) &&
      plannedFromStash.current &&
      plannedFromStash.previous
    ) {
      const deltaMinutes = differenceInMinutes(plannedFromStash.current?.toDate(), plannedFromStash.previous?.toDate())
      const nextTo = DateTime.fromDate(addMinutes(this.ctx.form.getRawValue().plannedTo?.toDate(), deltaMinutes))
      if (nextTo) {
        this.patchPlannedTo(nextTo)
      }
    }
  }

  private adaptPlannedFromByDelta(plannedToStash: SimpleStash<DateTime>) {
    if (
      plannedToStash.isChanged((a, b) => equal(a.time, b.time)) &&
      plannedToStash.current &&
      plannedToStash.previous
    ) {
      const deltaMinutes = differenceInMinutes(plannedToStash.current?.toDate(), plannedToStash.previous?.toDate())
      const rawFrom = this.ctx.form.getRawValue().plannedFrom
      if (!rawFrom?.isComplete) {
        return
      }
      const nextFrom = DateTime.fromDate(addMinutes(rawFrom.toDate(), deltaMinutes))
      if (nextFrom) {
        this.patchPlannedFrom(nextFrom)
      }
    }
  }

  private autoFillPlannedTo(plannedFromValue: DateTime) {
    const plannedFromDate = plannedFromValue.toDate()
    const plannedToDate = addMinutes(plannedFromDate, 60)
    const plannedToDateTime = DateTime.fromDate(plannedToDate)
    if (plannedToDateTime) {
      this.patchPlannedTo(plannedToDateTime)
    }
  }

  private autoFillPlannedFrom(plannedToValue: DateTime) {
    const plannedFromDate = addMinutes(plannedToValue.toDate(), -60)
    const plannedFromDateTime = DateTime.fromDate(plannedFromDate)
    if (plannedFromDateTime) {
      this.patchPlannedFrom(plannedFromDateTime)
    }
  }

  private async handleClientIdChange(clientId: number) {
    console.log(`${this.TAG}#handleClientIdChange ${clientId}`)
    if (!clientId) return

    const client = await firstValueFrom(this.ctx.clientsService.getById(clientId))
    this.loadDefaultClientValuesIntoForm(client)
  }

  private loadDefaultClientValuesIntoForm(client: Client) {
    console.log(this.TAG, 'loadDefaultClientValues', client)

    this.ctx.form.controls.keyCoordinatorId.markAsDirty()
    this.ctx.form.controls.preferredAssistantGenderId.markAsDirty()
    this.ctx.form.controls.strongAssistantNeeded.markAsDirty()
    this.ctx.form.controls.advancedAssistantNeeded.markAsDirty()

    this.ctx.form.patchValue(
      {
        keyCoordinatorId: client.keyWorkerUserId,
        preferredAssistantGenderId: client.preferredGenderId,
        strongAssistantNeeded: client.needsStrongAssistant,
        advancedAssistantNeeded: client.needsAdvancedAssistant,
      },
      { emitEvent: true },
    )
  }

  private handleFormControlsDisabledStates(
    form: AssistanceEditorForm = this.ctx.form,
    assistance: EditorAssistance = this.ctx.assistance,
    statuses = this.statuses,
  ) {
    function getEnablingRules() {
      const c = form.controls

      const clientId = c.clientId.value
      const assistantAssignments = c.assistantAssignments.value
      const statusId = c.statusId.value
      const status = statuses.find((s) => s.id === statusId)
      const isBilled = assistance.isBilled

      const rules: AssistanceEditorControlEnabledConditions = {
        clientId: () => (!assistantAssignments || !assistantAssignments.length) && !isBilled,
        urid: () => !!(clientId && !isBilled),
        plannedFrom: () =>
          !!(
            clientId &&
            !isBilled &&
            status.isOneOf([AssistanceStatusName.PLANNED, AssistanceStatusName.HAS_ASSISTANT])
          ),
        plannedTo: () =>
          !!(
            clientId &&
            !isBilled &&
            status.isOneOf([AssistanceStatusName.PLANNED, AssistanceStatusName.HAS_ASSISTANT])
          ),
        realizedFrom: () =>
          !!(
            clientId &&
            status.isAtLeast(AssistanceStatusName.CONFIRMED) &&
            assistantAssignments &&
            assistantAssignments.length &&
            !isBilled
          ),
        realizedTo: () => !!c.realizedFrom.enabled,
        addressStart: () => !!(clientId && !isBilled),
        addressEnd: () => !!(clientId && !isBilled),
        statusId: () => !!(clientId && !isBilled),
        keyCoordinatorId: () => !!(clientId && !isBilled),
        assistantAssignments: () => !!(clientId && !isBilled),
        isSentToClient: () => !!(clientId && !isBilled),
        coordinatorNote: () => !!(clientId && !isBilled),
        shortDescription: () => !!(clientId && !isBilled),
        detailedDescription: () => !!(clientId && !isBilled),
        isPaidInternally: () => !!(clientId && !isBilled),
        isNotToBePrimarilyAssigned: () => !!(clientId && !isBilled),
        isLateOrder: () => !!(clientId && !isBilled),
        preferredAssistantGenderId: () => !!(clientId && !isBilled),
        strongAssistantNeeded: () => !!(clientId && !isBilled),
        advancedAssistantNeeded: () => !!(clientId && !isBilled),
        isSleepover: () => !!(clientId && !isBilled),
        nonClientWork: () => !!(clientId && !isBilled),
        isShowInExchange: () => !!(clientId && !isBilled),
        journalEntries: () => !!clientId,
        rrule: () => !!(clientId && !isBilled),
      }

      return rules
    }

    const currentRules = getEnablingRules()

    for (const controlName in form.controls) {
      const control: FormControl = form.controls[controlName]
      if (currentRules[controlName]) {
        const targetState = currentRules[controlName]()
        console.log('targetState', targetState)
        if (targetState && control.disabled) {
          control.enable({ emitEvent: false })
        } else if (!targetState && control.enabled) {
          control.disable({ emitEvent: false })
        }
      }
    }

    this.ctx.recurrenceEditorShowManager.setReason('assistantMissing', !this.ctx.assistance.hasAssignments())
    this.ctx.recurrenceEditorShowManager.setReason('disabledPlannedTimes', this.c.plannedFrom.disabled)
  }

  private handleRruleChange(rrule: string) {
    if (rrule) this.c.isShowInExchange.patchValue(false)
  }

  // TODO: Incorporate!!
  // this.form.get('assistantAssignments').valueChanges
  //     .pipe(takeUntil(this.unsubscribe$))
  //     .subscribe((assignmentList: AssistantAssignmentList) => {
  //
  //       // Allow adding assistant only for currentOccurrence
  //       if (this.recurrenceEditingMode !== RecurrenceEditingMode.none) this.changeRecurrenceEditingMode(RecurrenceEditingMode.currentOccurrence)
  //
  //     })
}
